import React, { useState, useRef, useEffect } from "react";
import { StopIcon } from "@heroicons/react/solid";
import axios from "axios";

import { setAlert } from "../../redux/actions/alert";
import { connect } from "react-redux";
import ProductForm from "./FormProduct";
import micro from "../../assets/microphone.png";
function StepTranscription({
  productData,
  setProductData,
  prevStep,
  nextStep,
  setAlert,
  isAuthenticated,
  handleCancel, // Prop para resetear el wizard en el padre
}) {
  // Estados para la lógica de audio y transcripción
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  // Estado para mostrar el formulario manualmente
  const [showForm, setShowForm] = useState(!!productData.name);
  // Estado para errores del formulario
  const [formErrors, setFormErrors] = useState({});

  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  // ===== FUNCIONES DE AUDIO =====
  const setupRecorder = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (e) => {
        chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, {
          type: MediaRecorder.isTypeSupported("audio/webm; codecs=opus")
            ? "audio/webm; codecs=opus"
            : "audio/webm",
        });
        setAudioBlob(blob);
        chunksRef.current = [];
        await processAudio(blob);
      };
    } catch (err) {
      console.error("Error accessing microphone:", err);
      setError("Permiso de micrófono denegado");
    }
  };

  const startRecording = async () => {
    setError("");
    await setupRecorder();
    mediaRecorderRef.current?.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    }
  };

  const processAudio = async (blob) => {
    setIsProcessing(true);
    try {
      const formDataAudio = new FormData();
      formDataAudio.append("audio", blob, "recording.webm");

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/product/process-audio/`,
        formDataAudio,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.error) {
        setError(response.data.error);
      } else {
        // Guardar la transcripción en productData
        setProductData((prev) => ({
          ...prev,
          transcript: response.data.transcript || "",
        }));
      }
    } catch (err) {
      setError("Error al procesar el audio");
    }
    setIsProcessing(false);
  };

  // ===== VALIDACIÓN BASADA EN EL MODELO =====
  const validateProductData = (data) => {
    let errors = {};

    // Nombre: requerido y máximo 255 caracteres
    if (!data.name || data.name.trim() === "") {
      errors.name = "El nombre del producto es requerido.";
    } else if (data.name.length > 255) {
      errors.name = "El nombre no puede exceder 255 caracteres.";
    }

    // Descripción: requerida
    if (!data.description || data.description.trim() === "") {
      errors.description = "La descripción es requerida.";
    }

    // Precio y Precio Comparativo: deben ser números válidos
    if (!data.price || isNaN(parseFloat(data.price))) {
      errors.price = "El precio debe ser un número válido.";
    }

    if (!data.quantity || isNaN(parseInt(data.quantity))) {
      errors.quantity = "La cantidad debe ser un número válido.";
    }

    // Tipo: si se selecciona, debe ser uno de los permitidos
    const allowedTypes = [
      "clothing",
      "furniture",
      "jewelry",
      "art",
      "books",
      "electronics",
      "collectibles",
    ];
    if (data.type && !allowedTypes.includes(data.type)) {
      errors.type = "El tipo seleccionado no es válido.";
    }

    // Condición: si se selecciona, debe ser una de las permitidas
    const allowedConditions = ["regular", "good", "excellent", "new"];
    if (data.condition && !allowedConditions.includes(data.condition)) {
      errors.condition = "La condición seleccionada no es válida.";
    }

    // Cantidad: debe ser entero no negativo
    if (
      data.quantity &&
      (!Number.isInteger(Number(data.quantity)) || Number(data.quantity) < 0)
    ) {
      errors.quantity = "La cantidad debe ser un entero no negativo.";
    }

    // Si el tipo es 'clothing', validar el tamaño (si se ingresó)
    if (data.type && data.type.toLowerCase() === "clothing") {
      const allowedSizes = ["small", "medium", "large", "extra_large"];
      if (data.size && !allowedSizes.includes(data.size)) {
        errors.size = "El tamaño seleccionado no es válido para ropa.";
      }
    }

    return errors;
  };

  // ===== CONFIRMAR TRANSCRIPCIÓN =====
  const confirmTranscription = async () => {
    if (!productData.transcript) return;
    setIsProcessing(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/product/extract-product-data/`,
        { transcript: productData.transcript }
      );
      if (response.data.error) {
        setError(response.data.error);
      } else {
        // Combinar los datos extraídos con los que ya existan en productData
        setProductData((prev) => ({
          ...prev,
          name: response.data.name || prev.name || "",
          description: response.data.description || prev.description || "",
          price:
            response.data.price != null
              ? String(response.data.price)
              : prev.price || "",
          type: response.data.type || prev.type || "",
          condition: response.data.condition || prev.condition || "",
          size:
            response.data.size != null
              ? String(response.data.size)
              : prev.size || "",
          color: response.data.color || prev.color || "",
          material: response.data.material || prev.material || "",
          brand: response.data.brand || prev.brand || "",
          quantity:
            response.data.quantity != null
              ? String(response.data.quantity)
              : prev.quantity || "",
          active:
            response.data.active != null ? response.data.active : prev.active,
          year:
            response.data.year != null
              ? String(response.data.year)
              : prev.year || "",
          bibliography: response.data.bibliography || prev.bibliography || [],
        }));
        
        setShowForm(true);
      }
    } catch (err) {
      console.error(err);
      setError("Error al confirmar la transcripción");
    }
    setIsProcessing(false);
  };

  // Permitir entrada manual
  const manualInput = () => {
    setShowForm(true);
  };

  // ===== REQUISITOS DEL FORMULARIO =====
  // Función que verifica que los campos requeridos estén completos y sean válidos
  const isFormComplete = () => {
    const requiredFields = [
      "name",
      "price",
      "description",
      "quantity",
      "type",
      "condition",
      "brand",
    ];
    return requiredFields.every((field) => productData[field]?.trim() !== "");
  };

  // Manejar el clic en "Siguiente" con validación
  const handleNext = () => {
    if (!showForm) {
      setAlert(
        "Completa la transcripción o ingresa manualmente la información.",
        "error"
      );
      return;
    }
    const errors = validateProductData(productData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setAlert("Por favor, corrige los errores en el formulario.", "error");
      return;
    }
    // Si la validación es exitosa, limpiar errores y avanzar
    setFormErrors({});
    nextStep();
  };

  const handlePrev = () => prevStep();
  const onCancel = () => {
    // Si el usuario no está autenticado, reiniciamos el wizard
    if (!isAuthenticated && handleCancel) {
      handleCancel();
    }
    // Cerrar el modal
    document.getElementById("my_modal_5").close();
  };

  return (
    <div className="flex flex-col h-[80vh] max-w-2xl mx-auto py-4">
      <div className="flex-1 overflow-auto px-6">
        {!showForm ? (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold mb-4 text-center">
              AI-Powered Product Transcription
            </h2>
            <div className="flex flex-col items-center gap-3 relative">
              <button
                onClick={isRecording ? stopRecording : startRecording}
                className={`btn btn-circle w-32 h-32 ${isRecording ? "" : ""} ${
                  isProcessing ? "opacity-50 cursor-not-allowed" : ""
                }`}
                disabled={isProcessing}
              >
                {isRecording ? (
                  <StopIcon className="h-20 w-20 text-black animate-pulse" />
                ) : (
                  <img src={micro} className="h-28 w-28 text-black" />
                )}
              </button>

              {!productData.transcript && (
                <button
                  onClick={manualInput}
                  className="btn  text-black sm:w-auto mt-20"
                  disabled={isProcessing}
                >
                  Enter Manually
                </button>
              )}
              <div
                id="info_popover"
                className="hidden absolute top-[-40px] w-80 p-3 bg-white border rounded shadow-lg text-sm text-gray-700 text-center"
              >
                Dictate a description of the product, price, and other relevant
                data for the AI to transcribe and pre-fill the fields.
              </div>
            </div>
            <div className="mt-4 text-center">
              {isRecording && (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-ping h-3 w-3 bg-red-500 rounded-full"></div>
                  <span className="text-red-500">Recording...</span>
                </div>
              )}
              {isProcessing && (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                  <span className="text-gray-600">Processing audio...</span>
                </div>
              )}
              {error && (
                <div className="mt-2">
                  <p className="text-red-500">{error}</p>
                  <button
                    onClick={startRecording}
                    className="btn bg-blue-500 text-white hover:bg-blue-600 mt-2"
                  >
                    Retry Recording
                  </button>
                </div>
              )}
            </div>
            {productData.transcript && (
              <div className="mt-4 p-4  px-4 max-w-full">
                {!isRecording && (
                  <>
                    <h3 className="font-semibold mb-2 text-center">
                      Generated Transcription:
                    </h3>
                    <p className="whitespace-pre-wrap text-gray-700 text-center">
                      {productData.transcript}
                    </p>
                    <p className="mt-2 text-sm text-gray-600 text-center">
                      Confirm the transcription to pre-fill the data or enter
                      manually.
                    </p>
                    <div className="mt-4 flex flex-wrap sm:flex-nowrap justify-center gap-2">
                      <button
                        onClick={confirmTranscription}
                        disabled={!productData.transcript || isProcessing}
                        className="btn bg-gray-300 text-black text-[10px] sm:text-xs px-2 sm:px-3 w-full sm:w-auto min-w-[140px] sm:min-w-[160px]"
                      >
                        {isProcessing ? (
                          <span className="flex items-center">
                            <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                            Processing...
                          </span>
                        ) : (
                          "Confirm Transcription"
                        )}
                      </button>
                      <button
                        onClick={manualInput}
                        className="btn bg-gray-300 text-black  text-[10px] sm:text-xs px-2 sm:px-3 w-full sm:w-auto min-w-[140px] sm:min-w-[160px]"
                        disabled={isProcessing}
                      >
                        Enter Manually
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <ProductForm
            productData={productData}
            setProductData={setProductData}
            formErrors={formErrors}
          />
        )}
      </div>
      {/* Action buttons */}
      <div className="modal-action px-4 py-3 border-t flex justify-between items-center gap-2 w-full">
        <button
          onClick={onCancel}
          className="btn btn-outline text-xs sm:text-sm px-3 sm:px-4 w-auto max-w-[100px]"
        >
          Cancel
        </button>
        <div className="flex gap-2">
          <button
            onClick={handlePrev}
            className="btn text-xs sm:text-sm px-3 sm:px-4 w-auto max-w-[100px]"
          >
            Previous
          </button>
          <button
            onClick={handleNext}
            disabled={!showForm || !isFormComplete()}
            className={`btn btn-success ${
              !showForm || !isFormComplete() ? "btn-disabled" : ""
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({});

export default connect(mapStateToProps, { setAlert })(StepTranscription);
